import styled from "@emotion/styled";
import { HTMLAttributes } from "react";
import { isActuator } from "utils/functions";
import { ReactComponent as TemperatureIcon } from "assets/svg/sensor/temperature.svg";
import { ReactComponent as HumidityIcon } from "assets/svg/sensor/humidity.svg";
import { ReactComponent as CO2Icon } from "assets/svg/sensor/CO2.svg";
import { ReactComponent as ECO2Icon } from "assets/svg/sensor/eCO2.svg";
import { ReactComponent as AmbientIcon } from "assets/svg/sensor/ambient-light.svg";
import { ReactComponent as MotionIcon } from "assets/svg/sensor/motion.svg";
import { ReactComponent as MoistureIcon } from "assets/svg/sensor/moisture.svg";
import { ReactComponent as PHIcon } from "assets/svg/sensor/ph.svg";
import { ReactComponent as ECIcon } from "assets/svg/sensor/EC.svg";
import { ReactComponent as DOIcon } from "assets/svg/sensor/DO.svg";
import { ReactComponent as SalinityIcon } from "assets/svg/sensor/salinity.svg";
import { ReactComponent as ORPIcon } from "assets/svg/sensor/ORP.svg";
import { ReactComponent as CurrentIcon } from "assets/svg/sensor/current.svg";
import { ReactComponent as WaterLevelIcon } from "assets/svg/sensor/water-level.svg";
import { ReactComponent as GenericAnalogIcon } from "assets/svg/sensor/generic-analog-input.svg";
import { ReactComponent as GenericDigitalIcon } from "assets/svg/sensor/generic-digital-input.svg";
import { ReactComponent as WindVaneIcon } from "assets/svg/sensor/wind-vane.svg";
import { ReactComponent as WindSpeedIcon } from "assets/svg/sensor/wind-speed-anemometer.svg";
import { ReactComponent as RainGaugeIcon } from "assets/svg/sensor/rain-gauge.svg";
import { ReactComponent as VOCIcon } from "assets/svg/sensor/VOC.svg";
import { ReactComponent as IAQIcon } from "assets/svg/sensor/IAQ.svg";
import { ReactComponent as PressureIcon } from "assets/svg/sensor/pressure.svg";
import { ReactComponent as NitrogenIcon } from "assets/svg/sensor/nitrogen.svg";
import { ReactComponent as PhosphorusIcon } from "assets/svg/sensor/phosphorus.svg";
import { ReactComponent as PotassiumIcon } from "assets/svg/sensor/potassium.svg";
import { ReactComponent as AmmoniaIcon } from "assets/svg/sensor/ammonia.svg";
import { ReactComponent as LeafVapour } from "assets/svg/sensor/leaf-vapour.svg";
import { ReactComponent as AirVapour } from "assets/svg/sensor/air-vapour.svg";
import { ReactComponent as WaterDetectionIcon } from "assets/svg/sensor/water-detection.svg";
import { ReactComponent as VoltageIcon } from "assets/svg/sensor/voltage.svg";
import { ReactComponent as ActivePowerIcon } from "assets/svg/sensor/active-power.svg";
import { ReactComponent as ApparentPowerIcon } from "assets/svg/sensor/apparent-power.svg";
import { ReactComponent as ReactivePowerIcon } from "assets/svg/sensor/reactive-power.svg";
import { ReactComponent as PowerFactorIcon } from "assets/svg/sensor/power-factor.svg";
import { ReactComponent as WaterFlowRateIcon } from "assets/svg/sensor/water-flow-rate.svg";
import { ReactComponent as WaterFlowVelocityIcon } from "assets/svg/sensor/water-flow-velocity.svg";
import { ReactComponent as WaterUpstreamIcon } from "assets/svg/sensor/water-upstream-strength.svg";
import { ReactComponent as WaterDownstreamIcon } from "assets/svg/sensor/water-downstream-strength.svg";
import { ReactComponent as WaterSignalIcon } from "assets/svg/sensor/water-flow-signal-quanlity.svg";
import { ReactComponent as WaterVolumnDayIcon } from "assets/svg/sensor/water-flow-volumn-day.svg";
import { ReactComponent as WaterVolumnMonthIcon } from "assets/svg/sensor/water-flow-volumn-month.svg";
import { ReactComponent as FrequencyIcon } from "assets/svg/sensor/frequency.svg";
import { ReactComponent as ActiveEnergyIcon } from "assets/svg/sensor/active-energy.svg";
import { ReactComponent as FlowVolumeIcon } from "assets/svg/sensor/flow-volume.svg";
import { ReactComponent as PCSIcon } from "assets/svg/pcs.svg";
import { ReactComponent as PmOneIcon } from "assets/svg/sensor/pm1.0.svg";
import { ReactComponent as PmTenIcon } from "assets/svg/sensor/pm10.svg";
import { ReactComponent as PmTwoFiveIcon } from "assets/svg/sensor/pm2.5.svg";
import { ReactComponent as HCHOIcon } from "assets/svg/sensor/hcho.svg";
import { ReactComponent as DoorIcon } from "assets/svg/sensor/door.svg";
import { ReactComponent as ActuatorIcon } from "assets/svg/actuator/actuator.svg";

type SensorIconProps = HTMLAttributes<HTMLDivElement> &
    JSX.IntrinsicAttributes & {
        sensorType: string;
        cls?: string | number;
        width?: string;
        height?: string;
    };

const SensorIconMap: Record<string, JSX.Element> = {
    "Temperature sensor": <TemperatureIcon />,
    "Humidity sensor": <HumidityIcon />,
    "CO2 sensor": <CO2Icon />,
    "Ambient light sensor": <AmbientIcon />,
    "Motion sensor": <MotionIcon />,
    "Moisture sensor": <MoistureIcon />,
    "pH sensor": <PHIcon />,
    "EC sensor": <ECIcon />,
    "DO sensor": <DOIcon />,
    "Salinity sensor": <SalinityIcon />,
    "ORP sensor": <ORPIcon />,
    "Current Sensor": <CurrentIcon />,
    "Water level sensor": <WaterLevelIcon />,
    "Generic Analog Input": <GenericAnalogIcon />,
    "Water Level": <WaterLevelIcon />,
    "Wind Vane": <WindVaneIcon />,
    "Wind Speed": <WindSpeedIcon />,
    "Air Temperature": <TemperatureIcon />,
    "Generic Digital Input": <GenericDigitalIcon />,
    "Anemometer Wind Speed": <WindSpeedIcon />,
    "Rainfall Gauge": <RainGaugeIcon />,
    "eCO2 sensor": <ECO2Icon />,
    "VOC sensor": <VOCIcon />,
    "IAQ sensor": <IAQIcon />,
    "Pressure sensor": <PressureIcon />,
    "Nitrogen sensor": <NitrogenIcon />,
    "Phosphorus sensor": <PhosphorusIcon />,
    "Potassium sensor": <PotassiumIcon />,
    "Water detection sensor": <WaterDetectionIcon />,
    "Ammonia sensor": <AmmoniaIcon />,
    "Air Vapour Pressure Deficit": <AirVapour />,
    "Leaf Vapour Pressure Deficit": <LeafVapour />,
    "Flow Rate sensor": <WaterFlowRateIcon />,
    "Flow Velocity Sensor": <WaterFlowVelocityIcon />,
    "Upstream Strength Sensor": <WaterUpstreamIcon />,
    "Downstream Strength Sensor": <WaterDownstreamIcon />,
    "Flow Signal Quality Sensor": <WaterSignalIcon />,
    "Flow Volume Current Day Sensor": <WaterVolumnDayIcon />,
    "Flow Volume Current Month Sensor": <WaterVolumnMonthIcon />,
    "Voltage sensor": <VoltageIcon />,
    "Active Power sensor": <ActivePowerIcon />,
    "Reactive Power sensor": <ReactivePowerIcon />,
    "Apparent Power sensor": <ApparentPowerIcon />,
    "Power Factor sensor": <PowerFactorIcon />,
    "Frequency sensor": <FrequencyIcon />,
    "Active Energy sensor": <ActiveEnergyIcon />,
    "Flow Volume sensor": <FlowVolumeIcon />,
    "Anemometer": <WindSpeedIcon />,
    "Wind Speed Anemometer": <WindSpeedIcon />,
    "Rain Gauge": <RainGaugeIcon />,
    "Generic DIN": <GenericDigitalIcon />,
    "generic din": <GenericDigitalIcon />,
    "IO Controller DIN": <GenericDigitalIcon />,
    "io controller din": <GenericDigitalIcon />,
    "Binary Digital Input": <GenericDigitalIcon />,
    "Generic AIN": <GenericAnalogIcon />,
    "generic ain": <GenericAnalogIcon />,
    "IO Controller AIN": <GenericAnalogIcon />,
    "io controller ain": <GenericAnalogIcon />,
    "People Count Sensor": <PCSIcon />,
    "PM1.0 Sensor": <PmOneIcon />,
    "PM2.5 Sensor": <PmTwoFiveIcon />,
    "PM10 Sensor": <PmTenIcon />,
    "HCHO Sensor": <HCHOIcon />,
    "Door Sensor": <DoorIcon />,
    "Flow Volume Positive Sensor": <FlowVolumeIcon />,
    "Flow Volume Negative Sensor": <FlowVolumeIcon />,
};

const SensorIconLowerMap: Record<string, any> = {};
Object.keys(SensorIconMap).forEach(
    (key: string) =>
        (SensorIconLowerMap[key.toLowerCase()] = SensorIconMap[key])
);

const Icon = styled.span<{ width?: string, height?: string }>`
    width: ${({ width }) => (width ?? "37px")};
    height: ${({ height }) => (height ?? "37px")};
    padding: 0;

    svg {
        width: 35px;
    }

    @media (max-width: 992px) {
        svg {
            width: 30px;
        }
    }
`;

const SensorIcon = ({ sensorType, cls, ...restProps }: SensorIconProps) => {
    if (!sensorType) return <span>{""}</span>;
    const icon =
        SensorIconLowerMap[sensorType.toLowerCase()] ??
        (cls != null && isActuator(Number(cls)) ? (
            <ActuatorIcon />
        ) : undefined);
    return <Icon {...restProps}>{icon}</Icon>;
};

export default SensorIcon;
